/**
 * FO-312 - Getypeerde placeholder-registry voor de document-generatie-formulieren.
 *
 * Eén centrale, code-gebaseerde registry (géén DB) die per BEKENDE placeholder-key
 * het veldtype, de opties, de bron en een eventuele conditie vastlegt. Gedeeld
 * door de frontend (`placeholder-field.tsx` rendert Select/DatePicker/EntityPicker/
 * decimal op basis hiervan; `groupPlaceholders` sorteert op registry-groep) én de
 * API (server-side validatie van aangeleverde waarden: `hij_zij ∈ {Hij, Zij}`,
 * decimal/int/datum-vorm). Zo kan het probleem "kaal tekstveld zonder type/bron/
 * conditie" nergens terugkomen, en blijft dit één bron-van-waarheid.
 *
 * Bewust in `packages/types` (geen runtime-afhankelijkheid van @streep/db): de
 * registry is code, reviewbaar via git (FO-312 §8). Het spiegelt de per-template
 * `PlaceholderConfig` (label/required/autoFillFrom uit de DB) NIET - het vult die
 * aan met het getypeerde gedrag voor bekende keys. Onbekende keys blijven een
 * tekstveld (nooit blokkeren) en worden gelogd zodat de registry kan bijgroeien.
 *
 * Normalisatie: keys worden alfanumeriek-lowercase genormaliseerd (`WERKNEMER_NAAM`,
 * `werknemer_naam` en `werknemernaam` → `werknemernaam`), gelijk aan de
 * alfanumerieke alias van de docx-render-resolver, zodat de rommelige
 * sjabloon-casing toch matcht.
 */

import type { PlaceholderConfig } from './document-templates'
import {
  canonicalPlaceholderKey,
  groepeerPlaceholderVarianten,
  isPlaceholderSectieSleutel,
  placeholderVariantNetheid,
} from './placeholder-canonicalisatie'

/** Veldtype in de registry - rijker dan de DB-`PlaceholderFieldType`. */
export type PlaceholderRegistryType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'date'
  | 'decimal'
  | 'int'
  | 'entity'
  | 'auto'

/** Registry-groep (FO-312 §2.1): bepaalt de veldvolgorde i.p.v. het alfabet. */
export type PlaceholderGroep =
  | 'persoon'
  // FO-401 (batch-49) - klantgegevens op relatie-overeenkomsten (naam,
  // vestigingsadres, contactpersoon). Stond eerder verspreid onder 'overig'.
  | 'klant'
  | 'contract'
  | 'BV'
  | 'vergoedingen'
  | 'ondertekening'
  | 'overig'

export const PLACEHOLDER_GROEP_VOLGORDE: readonly PlaceholderGroep[] = [
  'persoon',
  'klant',
  'contract',
  'BV',
  'vergoedingen',
  'ondertekening',
  'overig',
] as const

/** NL-label per registry-groep (visuele kop in het formulier). */
export const PLACEHOLDER_GROEP_LABELS: Record<PlaceholderGroep, string> = {
  persoon:       'Persoonsgegevens',
  klant:         'Klantgegevens',
  contract:      'Contractgegevens',
  BV:            'STREEP-entiteit',
  vergoedingen:  'Vergoedingen',
  ondertekening: 'Ondertekening',
  overig:        'Overig',
}

/** Definitie van één bekende placeholder-key. */
export interface PlaceholderFieldDef {
  /** Veldtype dat het formulier rendert. */
  type: PlaceholderRegistryType
  /** Registry-groep (veldvolgorde). */
  groep: PlaceholderGroep
  /** Opties voor `type: 'select'` (bijv. hij_zij → Hij/Zij). */
  opties?: ReadonlyArray<{ value: string; label: string }>
  /**
   * Bron-key voor documentatie/afleiding: de `autoFillFrom`-spec of resolver-key
   * die dit veld voedt (bijv. `entity.name`, `employee.geslacht`). Puur
   * beschrijvend; de daadwerkelijke autofill blijft via de resolver lopen.
   */
  bronKey?: string
  /**
   * Conditie-vlag: dit veld is alléén van toepassing (en zichtbaar) wanneer de
   * genoemde vlag "aan" staat. Nu gebruikt voor `is_verlenging` (vorig-contract-
   * velden). Zonder conditie is het veld altijd van toepassing.
   */
  conditie?: string
  /** Default-label (gebruikt als de DB-config er geen heeft). */
  label?: string
  /**
   * Verre-datum-veld → de DatePicker krijgt jaar-dropdowns (geboortedatum e.d.).
   * Alleen zinvol bij `type: 'date'`.
   */
  verreDatum?: boolean
}

/** De conditie-vlag voor het vorig-contract-blok (docxtemplater `{{#is_verlenging}}`). */
export const CONDITIE_IS_VERLENGING = 'is_verlenging'

/**
 * batch-39 (FO-337 §2.5) — conditie-vlag voor het CAO-schaal/trede-blok. Staat
 * de organisatie-instelling `werktMetCaoSchalen` uit (default), dan zijn de
 * CAO-placeholders NIET van toepassing: het formulier toont ze niet en het
 * docxtemplater-blok `{{#werkt_met_cao}}...{{/werkt_met_cao}}` wordt weggelaten.
 */
export const CONDITIE_WERKT_MET_CAO = 'werkt_met_cao'

/** NL-maandnamen → maandnummer, voor het parsen van lange datums ("1 juni 2026"). */
const NL_MAANDEN: Readonly<Record<string, number>> = {
  januari: 1, februari: 2, maart: 3, april: 4, mei: 5, juni: 6,
  juli: 7, augustus: 8, september: 9, oktober: 10, november: 11, december: 12,
}

/**
 * Zet een opgeslagen/autofill-datum om naar ISO (`yyyy-MM-dd`) voor de DatePicker
 * `value`. De resolvers leveren datums als `dd-MM-yyyy` (persoonsdatums) of NL-lang
 * ("1 juni 2026"); de DatePicker parseert echter alleen ISO, dus zonder deze
 * conversie tonen autofill-datums LEEG (FO-312 - anders lijkt een voorgevuld veld
 * onterecht leeg). Onparseerbaar → undefined (het veld toont leeg, geen crash).
 */
export function placeholderDateToIso(value: string | undefined): string | undefined {
  if (!value) return undefined
  const v = value.trim()
  // Reeds ISO (evt. met tijd) → alleen de datum.
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  // dd-MM-yyyy of d-M-yyyy (numeriek).
  const dmy = v.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  // NL-lang: "1 juni 2026".
  const lang = v.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/)
  if (lang) {
    const maand = NL_MAANDEN[lang[2]!.toLowerCase()]
    if (maand) return `${lang[3]}-${String(maand).padStart(2, '0')}-${lang[1]!.padStart(2, '0')}`
  }
  return undefined
}

/**
 * Zet de ISO-uitvoer van de DatePicker (`yyyy-MM-dd`) om naar de opgeslagen
 * `dd-MM-yyyy`-notatie (CLAUDE.md-datumstandaard + consistent met de resolver-
 * datums, zodat het document leesbaar NL rendert). Niet-ISO → ongewijzigd terug.
 */
export function isoToPlaceholderDate(iso: string): string {
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso
}

/** De twee toegestane hij/zij-waarden (sjabloon-conform; FO-312 §12). */
export const HIJ_ZIJ_OPTIES = [
  { value: 'Hij', label: 'Hij' },
  { value: 'Zij', label: 'Zij' },
] as const

/**
 * Normaliseer een placeholder-key tot de registry-lookup-vorm, zodat
 * `WERKNEMER_NAAM` / `werknemer_naam` / `werknemernaam` alle drie op dezelfde def
 * matchen.
 *
 * FO-410 (batch-54): dit is exact de CANONIEKE key uit
 * `placeholder-canonicalisatie.ts` - één implementatie voor render-resolver,
 * scanner, wizard, config-editor en deze registry. Deze naam blijft bestaan voor
 * de bestaande importeurs; nieuwe code gebruikt `canonicalPlaceholderKey`.
 */
export function normalizePlaceholderRegistryKey(key: string): string {
  return canonicalPlaceholderKey(key)
}

/**
 * De getypeerde definities per (genormaliseerde) placeholder-key. Meerdere
 * spellingsvarianten die naar dezelfde def wijzen worden onder hun genormaliseerde
 * vorm samengevoegd (bijv. `werknemer_naam` en `WERKNEMER_NAAM` → `werknemernaam`).
 * De helper-builder onderaan normaliseert de sleutels, zodat je hier leesbaar mag
 * schrijven.
 */
const RAW_FIELD_DEFS: Record<string, PlaceholderFieldDef> = {
  // ── Persoon ────────────────────────────────────────────────────────────────
  hij_zij: {
    type: 'select',
    groep: 'persoon',
    opties: HIJ_ZIJ_OPTIES,
    bronKey: 'employee.hij_zij',
    label: 'Hij / Zij',
  },
  aanhef: { type: 'auto', groep: 'persoon', bronKey: 'employee.aanhef', label: 'Aanhef' },
  werknemer_naam: { type: 'auto', groep: 'persoon', bronKey: 'employee.naam', label: 'Naam werknemer' },
  naam: { type: 'auto', groep: 'persoon', bronKey: 'employee.naam', label: 'Naam' },
  voornaam: { type: 'auto', groep: 'persoon', bronKey: 'employee.voornaam', label: 'Voornaam' },
  achternaam: { type: 'auto', groep: 'persoon', bronKey: 'employee.achternaam', label: 'Achternaam' },
  voorletter: { type: 'auto', groep: 'persoon', bronKey: 'employee.voorletter', label: 'Voorletters' },
  werknemer_adres: { type: 'auto', groep: 'persoon', bronKey: 'employee.adres', label: 'Adres werknemer' },
  adres: { type: 'auto', groep: 'persoon', bronKey: 'employee.adres', label: 'Adres' },
  straatnaam: { type: 'auto', groep: 'persoon', bronKey: 'employee.straatnaam', label: 'Straat' },
  huisnummer: { type: 'auto', groep: 'persoon', bronKey: 'employee.huisnummer', label: 'Huisnummer' },
  werknemer_postcode_plaats: { type: 'auto', groep: 'persoon', bronKey: 'employee.postcodePlaats', label: 'Postcode + plaats' },
  postcode: { type: 'auto', groep: 'persoon', bronKey: 'employee.postcode', label: 'Postcode' },
  woonplaats: { type: 'auto', groep: 'persoon', bronKey: 'employee.woonplaats', label: 'Woonplaats' },
  werknemer_geboortedatum: { type: 'date', groep: 'persoon', bronKey: 'employee.geboortedatum', label: 'Geboortedatum', verreDatum: true },
  geboortedatum: { type: 'date', groep: 'persoon', bronKey: 'employee.geboortedatum', label: 'Geboortedatum', verreDatum: true },
  geboorteplaats: { type: 'auto', groep: 'persoon', bronKey: 'employee.geboortePlaats', label: 'Geboorteplaats' },
  nationaliteit: { type: 'auto', groep: 'persoon', bronKey: 'employee.nationaliteit', label: 'Nationaliteit' },
  werknemer_bsn: { type: 'auto', groep: 'persoon', bronKey: 'employee.bsn', label: 'BSN' },
  bsn: { type: 'auto', groep: 'persoon', bronKey: 'employee.bsn', label: 'BSN' },
  iban: { type: 'auto', groep: 'persoon', bronKey: 'employee.iban', label: 'IBAN' },

  // ── Klant (FO-401, relatie-overeenkomsten) ───────────────────────────────
  // De klantnaam zoals ingevoerd én de rechtsvorm-vrije variant, zodat een
  // sjabloonzin met een vaste "B.V." nooit "A. Jansen B.V. B.V." oplevert.
  klantnaam: { type: 'auto', groep: 'klant', bronKey: 'client.name', label: 'Naam klant' },
  klantnaam_zonder_rechtsvorm: {
    type: 'auto',
    groep: 'klant',
    bronKey: 'client.naamZonderRechtsvorm',
    label: 'Naam klant (zonder rechtsvorm)',
  },
  klant_adres: { type: 'auto', groep: 'klant', bronKey: 'client.vestigingsAdres', label: 'Adres klant (straat + huisnummer)' },
  klant_postcode_plaats: { type: 'auto', groep: 'klant', bronKey: 'client.vestigingsPostcodePlaats', label: 'Postcode + plaats klant' },
  klant_vestigingsadres: { type: 'auto', groep: 'klant', bronKey: 'client.vestigingsadres', label: 'Vestigingsadres klant (volledig)' },

  // ── Contract ─────────────────────────────────────────────────────────────
  functie: { type: 'auto', groep: 'contract', bronKey: 'employee.functie', label: 'Functie' },
  afdeling: { type: 'auto', groep: 'contract', bronKey: 'employee.afdeling', label: 'Afdeling' },
  startdatum: { type: 'date', groep: 'contract', bronKey: 'employee.startDatum', label: 'Startdatum' },
  datum_indiensttreding: { type: 'date', groep: 'contract', bronKey: 'employee.startDatum', label: 'Datum indiensttreding' },
  einddatum: { type: 'date', groep: 'contract', bronKey: 'employee.eindDatum', label: 'Einddatum' },
  uren_per_week: { type: 'int', groep: 'contract', bronKey: 'employee.uren', label: 'Uren per week' },
  uren: { type: 'int', groep: 'contract', bronKey: 'employee.uren', label: 'Uren per week' },
  salaris: { type: 'decimal', groep: 'contract', bronKey: 'employee.salaris', label: 'Bruto maandsalaris' },
  bruto_maandsalaris: { type: 'decimal', groep: 'contract', bronKey: 'employee.salaris', label: 'Bruto maandsalaris' },
  uurloon: { type: 'decimal', groep: 'contract', bronKey: 'employee.uurloon', label: 'Uurloon' },
  // batch-39 (FO-337 §2.5) — CAO-schaal/trede alleen van toepassing wanneer de
  // organisatie met CAO-schalen werkt (conditie CONDITIE_WERKT_MET_CAO).
  salarisschaal: { type: 'auto', groep: 'contract', bronKey: 'employee.caoSchaal', conditie: CONDITIE_WERKT_MET_CAO, label: 'Salarisschaal' },
  caoschaal: { type: 'auto', groep: 'contract', bronKey: 'employee.caoSchaal', conditie: CONDITIE_WERKT_MET_CAO, label: 'CAO-schaal' },
  caotrede: { type: 'int', groep: 'contract', bronKey: 'employee.caoTrede', conditie: CONDITIE_WERKT_MET_CAO, label: 'CAO-trede' },
  functiefamilie_nr: { type: 'text', groep: 'contract', label: 'Functiefamilie-nr' },
  proeftijd: { type: 'int', groep: 'contract', label: 'Proeftijd (maanden)' },
  contracttype: { type: 'auto', groep: 'contract', bronKey: 'employee.contractType', label: 'Contractvorm' },
  // Vorig contract - alleen bij verlenging (conditie), voorgevuld uit het vorige contract.
  vorig_contract_duur: {
    type: 'int',
    groep: 'contract',
    conditie: CONDITIE_IS_VERLENGING,
    label: 'Duur vorig tijdelijk contract (maanden)',
  },
  vorig_contract_einde: {
    type: 'date',
    groep: 'contract',
    conditie: CONDITIE_IS_VERLENGING,
    label: 'Einddatum vorig contract',
  },
  vorig_contract_start: {
    type: 'date',
    groep: 'contract',
    conditie: CONDITIE_IS_VERLENGING,
    label: 'Startdatum vorig contract',
  },

  // ── BV / STREEP-entiteit ─────────────────────────────────────────────────
  // De BV-naam-anchor: type 'entity' → EntityPicker; de keuze zet de afgeleiden.
  bv_streep: { type: 'entity', groep: 'BV', bronKey: 'entity.name', label: 'STREEP BV' },
  streep_entiteit: { type: 'auto', groep: 'BV', bronKey: 'entity.name', label: 'STREEP BV naam' },
  bvnaam: { type: 'auto', groep: 'BV', bronKey: 'entity.name', label: 'BV naam' },
  entiteit: { type: 'auto', groep: 'BV', bronKey: 'entity.name', label: 'STREEP BV' },
  werkgevernaam: { type: 'auto', groep: 'BV', bronKey: 'entity.name', label: 'Werkgever' },
  streep_kvk: { type: 'auto', groep: 'BV', bronKey: 'entity.kvkNumber', label: 'STREEP KVK' },
  bvkvk: { type: 'auto', groep: 'BV', bronKey: 'entity.kvkNumber', label: 'KVK' },
  kvk: { type: 'auto', groep: 'BV', bronKey: 'entity.kvkNumber', label: 'KVK-nummer' },
  streep_adres: { type: 'auto', groep: 'BV', bronKey: 'entity.street', label: 'STREEP adres' },
  bvadres: { type: 'auto', groep: 'BV', bronKey: 'entity.fullAddress', label: 'BV adres' },
  streep_postcode_plaats: { type: 'auto', groep: 'BV', bronKey: 'entity.postalCode', label: 'STREEP postcode + plaats' },

  // ── Vergoedingen ─────────────────────────────────────────────────────────
  reiskosten_per_km: { type: 'decimal', groep: 'vergoedingen', label: 'Reiskosten per km' },
  kilometervergoeding: { type: 'decimal', groep: 'vergoedingen', label: 'Kilometervergoeding' },
  enkele_reisafstand: { type: 'int', groep: 'vergoedingen', bronKey: 'employee.enkeleReisafstand', label: 'Enkele reisafstand (km)' },
  reisafstand: { type: 'int', groep: 'vergoedingen', bronKey: 'employee.enkeleReisafstand', label: 'Reisafstand woon-werk (km)' },

  // ── Ondertekening ────────────────────────────────────────────────────────
  datum_ondertekening: { type: 'date', groep: 'ondertekening', bronKey: 'today', label: 'Datum ondertekening' },
  plaats_ondertekening: { type: 'auto', groep: 'ondertekening', bronKey: 'entity.city', label: 'Plaats ondertekening' },
  ondertekenaar: { type: 'auto', groep: 'ondertekening', bronKey: 'entity.ondertekenaarNaam', label: 'Ondertekenaar' },
  // FO-401 (batch-49) - beide namen onder de handtekeninglijnen van het
  // twee-koloms ondertekenblok (opdrachtgever links, STREEP rechts).
  ondertekenaar_klant: { type: 'auto', groep: 'ondertekening', bronKey: 'client.contactpersoonNaam', label: 'Ondertekenaar klant' },
  ondertekenaar_streep: { type: 'auto', groep: 'ondertekening', bronKey: 'entity.ondertekenaarNaam', label: 'Ondertekenaar STREEP' },
  ondertekenaarfunctie: { type: 'auto', groep: 'ondertekening', bronKey: 'entity.ondertekenaarFunctie', label: 'Ondertekenaar (functie)' },
}

/**
 * De registry, gekeyed op de genormaliseerde placeholder-key. Afgeleid uit
 * `RAW_FIELD_DEFS` zodat de leesbare (met underscores gespelde) sleutels hierboven
 * naar hun genormaliseerde vorm samenvallen. Bij een collision (twee raw-keys die
 * naar dezelfde genormaliseerde key normaliseren) wint de eerste - houd de
 * raw-keys daarom betekenis-consistent.
 */
export const PLACEHOLDER_FIELD_DEFS: Readonly<Record<string, PlaceholderFieldDef>> = (() => {
  const out: Record<string, PlaceholderFieldDef> = {}
  for (const [rawKey, def] of Object.entries(RAW_FIELD_DEFS)) {
    const norm = normalizePlaceholderRegistryKey(rawKey)
    if (!(norm in out)) out[norm] = def
  }
  return out
})()

/**
 * De getypeerde def voor een placeholder-key, of `undefined` als de key niet in
 * de registry staat (→ de caller valt terug op een tekstveld en logt de key).
 * Case-/scheidingsteken-ongevoelig.
 */
export function getPlaceholderFieldDef(key: string): PlaceholderFieldDef | undefined {
  return PLACEHOLDER_FIELD_DEFS[normalizePlaceholderRegistryKey(key)]
}

/** Is deze key bekend in de registry? */
export function isKnownPlaceholderKey(key: string): boolean {
  return normalizePlaceholderRegistryKey(key) in PLACEHOLDER_FIELD_DEFS
}

// ── Effectief renderbaar veld (merge registry + DB-config) ──────────────────

/** De DB-`PlaceholderFieldType` → registry-type-projectie voor ONBEKENDE keys. */
const DB_TYPE_TO_REGISTRY: Record<string, PlaceholderRegistryType> = {
  text:     'text',
  textarea: 'textarea',
  date:     'date',
  number:   'int',
  currency: 'decimal',
  select:   'select',
}

/** Het effectief te renderen veld: registry wint voor bekende keys, DB-config vult aan. */
export interface ResolvedPlaceholderField {
  /** Het type dat het formulier rendert. */
  type: PlaceholderRegistryType
  /** De (NL-)groepslabel voor de visuele indeling. */
  groepLabel: string
  /** Opties voor select/entity (registry wint; anders de DB-config-opties). */
  opties?: ReadonlyArray<{ value: string; label: string }>
  /** Conditie-vlag (alleen tonen als de vlag aan staat), of undefined. */
  conditie?: string
  /** Verre-datum → DatePicker met jaar-dropdowns (geboortedatum e.d.). */
  verreDatum: boolean
  /** Staat de key in de registry? (false → tekstveld-fallback + log). */
  isKnown: boolean
}

/** Minimale config-shape die `resolvePlaceholderField` nodig heeft. */
type ResolvableConfig = Pick<PlaceholderConfig, 'key' | 'type' | 'group' | 'options'>

/**
 * Bepaal het effectief renderbare veld voor een placeholder. Bekende key → de
 * registry is leidend voor type/opties/groep/conditie (kan nooit regresseren naar
 * vrij tekstveld waar getypt hoort). Onbekende key → de DB-config-`type` (door de
 * admin ingesteld) met tekst als vangnet; `isKnown=false` zodat de caller kan loggen.
 */
export function resolvePlaceholderField(config: ResolvableConfig): ResolvedPlaceholderField {
  const def = getPlaceholderFieldDef(config.key)
  if (def) {
    return {
      type:       def.type,
      groepLabel: PLACEHOLDER_GROEP_LABELS[def.groep],
      opties:     def.opties ?? config.options,
      conditie:   def.conditie,
      verreDatum: def.verreDatum ?? false,
      isKnown:    true,
    }
  }
  return {
    type:       DB_TYPE_TO_REGISTRY[config.type] ?? 'text',
    groepLabel: config.group?.trim() || 'Overig',
    opties:     config.options,
    verreDatum: false,
    isKnown:    false,
  }
}

// ── FO-410 (batch-54): canonieke dedupe van schrijfwijze-varianten ──────────

/** Eén samengevoegde config-rij: de varianten die tot dit veld zijn gesmolten. */
export interface PlaceholderConfigSamenvoeging {
  /** De canonieke key waarop is samengevoegd. */
  canoniekeKey: string
  /** De behouden (netste) variant - dit is de `key` van de resulterende rij. */
  behoudenKey: string
  /** Alle schrijfwijzen die op deze rij samenvallen (incl. `behoudenKey`). */
  varianten: string[]
}

/** Resultaat van `dedupePlaceholderConfig`. */
export interface PlaceholderConfigDedupe {
  /** Eén rij per canoniek gegeven, in volgorde van eerste voorkomen. */
  config: PlaceholderConfig[]
  /** Alleen de rijen waar écht is samengevoegd (>= 2 varianten) - voor de log/UI. */
  samenvoegingen: PlaceholderConfigSamenvoeging[]
}

/** Is dit label nietszeggend (gelijk aan de key zelf)? Dan wint een echt label. */
function isKaalLabel(config: PlaceholderConfig): boolean {
  const label = (config.label ?? '').trim()
  if (!label) return true
  return canonicalPlaceholderKey(label) === canonicalPlaceholderKey(config.key)
}

/**
 * FO-410 §A1/§6.1 - voeg twee configs van dezelfde canonieke key samen: de
 * RIJKSTE wint per eigenschap.
 *
 * - `required`: verplicht wint van optioneel (een sjabloon dat het gegeven ergens
 *   verplicht stelt, blijft verplicht);
 * - `label`: een specifiek label wint van een label dat de key herhaalt;
 * - `type`/`group`/`defaultValue`/`options`/`autoFillFrom`: de eerste die een
 *   betekenisvolle waarde draagt (basis = de netste variant, aanvulling = de rest).
 */
function voegConfigsSamen(basis: PlaceholderConfig, extra: PlaceholderConfig): PlaceholderConfig {
  const neemLabel = isKaalLabel(basis) && !isKaalLabel(extra)
  return {
    ...basis,
    label: neemLabel ? extra.label : basis.label,
    required: basis.required || extra.required,
    type: basis.type === 'text' && extra.type !== 'text' ? extra.type : basis.type,
    group:
      !basis.group?.trim() || basis.group.trim() === 'Algemeen'
        ? (extra.group?.trim() ? extra.group : basis.group)
        : basis.group,
    ...(basis.defaultValue?.trim()
      ? {}
      : extra.defaultValue?.trim()
        ? { defaultValue: extra.defaultValue }
        : {}),
    ...(basis.options?.length ? {} : extra.options?.length ? { options: extra.options } : {}),
    ...(basis.autoFillFrom?.trim()
      ? {}
      : extra.autoFillFrom?.trim()
        ? { autoFillFrom: extra.autoFillFrom }
        : {}),
  }
}

/**
 * FO-410 §A - dedupliceer een placeholder-config op canonieke key: één rij per
 * gegeven, ook als het sjabloon `{{OPDRACHTGEVER_NAAM}}` én `{{Opdrachtgever naam}}`
 * bevat. De behouden `key` is de netste variant (die blijft de veld-key); de
 * render vult daarna álle varianten (`vulPlaceholderVarianten`).
 *
 * Sectie-sleutels (`#x`/`/x`/`^x`) blijven ongemoeid en ongededupliceerd - het
 * zijn render-controle-tokens, geen invulvelden.
 */
export function dedupePlaceholderConfig(
  config: readonly PlaceholderConfig[],
): PlaceholderConfigDedupe {
  const perCanoniek = new Map<string, { rij: PlaceholderConfig; varianten: string[] }>()
  const volgorde: string[] = []
  const doorgelaten: PlaceholderConfig[] = []

  for (const rij of config) {
    const key = (rij.key ?? '').trim()
    if (!key || isPlaceholderSectieSleutel(key)) {
      doorgelaten.push(rij)
      continue
    }
    const canoniek = canonicalPlaceholderKey(key)
    if (!canoniek) {
      doorgelaten.push(rij)
      continue
    }
    const bestaand = perCanoniek.get(canoniek)
    if (!bestaand) {
      perCanoniek.set(canoniek, { rij, varianten: [key] })
      volgorde.push(canoniek)
      continue
    }
    if (!bestaand.varianten.includes(key)) bestaand.varianten.push(key)
    // De NETSTE variant levert de behouden key/labelbasis; de andere vult aan.
    const basisIsNetter =
      placeholderVariantNetheid(bestaand.rij.key) >= placeholderVariantNetheid(key)
    bestaand.rij = basisIsNetter
      ? voegConfigsSamen(bestaand.rij, rij)
      : voegConfigsSamen(rij, bestaand.rij)
  }

  const samenvoegingen: PlaceholderConfigSamenvoeging[] = []
  const out: PlaceholderConfig[] = []
  for (const canoniek of volgorde) {
    const groep = perCanoniek.get(canoniek)!
    out.push(groep.rij)
    if (groep.varianten.length > 1) {
      samenvoegingen.push({
        canoniekeKey: canoniek,
        behoudenKey: groep.rij.key,
        varianten: groep.varianten,
      })
    }
  }
  return { config: [...out, ...doorgelaten], samenvoegingen }
}

/**
 * Hoeveel schrijfwijzen kent dit canonieke gegeven in de gescande DOCX? Voedt de
 * "komt {n}x voor in het sjabloon"-hint in de config-editor. Onbekende key → 0.
 */
export function telPlaceholderVarianten(
  scannedKeys: readonly string[],
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const groep of groepeerPlaceholderVarianten(scannedKeys)) {
    out[groep.canoniekeKey] = groep.aantal
  }
  return out
}

// ── Groepering + volgorde (registry-groepen i.p.v. alfabet) ─────────────────

/** Volgorde-index van een groepslabel: registry-groepen eerst (vaste volgorde). */
const REGISTRY_GROEP_LABEL_VOLGORDE: readonly string[] = PLACEHOLDER_GROEP_VOLGORDE.map(
  (g) => PLACEHOLDER_GROEP_LABELS[g],
)

/**
 * Groepeer placeholders op hun (registry-)groep en sorteer de groepen volgens de
 * registry-volgorde (persoon · contract · BV · vergoedingen · ondertekening ·
 * overig), i.p.v. het alfabet (FO-312 §2.1). Onbekende/DB-only groepen volgen
 * daarna, op eerste-voorkomen-volgorde. Binnen een groep blijft de configuratie-
 * volgorde behouden.
 *
 * `opts.activeConditions`: velden met een registry-conditie (bijv. `is_verlenging`)
 * die NIET in deze set zit worden weggelaten (vorig-contract-velden bij een eerste
 * contract). Zonder deze optie zijn er geen actieve condities, dus conditie-velden
 * blijven verborgen - de veilige default (geen vorig-contract-velden tonen zonder
 * expliciete verlenging).
 *
 * Docxtemplater-sectiesleutels (`{{#x}}`/`{{/x}}` → keys `#x`/`/x` na een DOCX-scan)
 * en de `is_verlenging`-vlag zelf worden nooit als veld gerenderd (het zijn geen
 * invoervelden maar render-controle) - FO-312, anders verschijnen er stray
 * "#is_verlenging"/"​/is_verlenging"-tekstvelden na een re-upload.
 *
 * FO-410 (batch-54): schrijfwijze-varianten van hetzelfde gegeven
 * (`OPDRACHTGEVER_NAAM` + `Opdrachtgever naam`) worden eerst op hun canonieke key
 * samengevoegd tot ÉÉN veld - alle formulieren die deze helper gebruiken tonen
 * daarmee één invulveld per gegeven. Bij het renderen vult die ene waarde alsnog
 * álle varianten in het document (`vulPlaceholderVarianten`).
 */
export function groupPlaceholders(
  config: readonly PlaceholderConfig[],
  opts?: { activeConditions?: ReadonlySet<string> },
): Array<[string, PlaceholderConfig[]]> {
  const active = opts?.activeConditions
  const groups = new Map<string, PlaceholderConfig[]>()
  const firstSeen = new Map<string, number>()
  let order = 0
  const flagKey = normalizePlaceholderRegistryKey(CONDITIE_IS_VERLENGING)
  for (const p of dedupePlaceholderConfig(config).config) {
    // Sectie-controle-sleutels (`#`/`/`/`^`-prefix) en de conditie-vlag zelf zijn
    // geen invoervelden → nooit renderen.
    const trimmed = p.key.trim()
    if (trimmed.startsWith('#') || trimmed.startsWith('/') || trimmed.startsWith('^')) continue
    if (normalizePlaceholderRegistryKey(p.key) === flagKey) continue
    const resolved = resolvePlaceholderField(p)
    // Conditie-veld dat niet actief is → verbergen.
    if (resolved.conditie && !(active?.has(resolved.conditie) ?? false)) continue
    const label = resolved.groepLabel
    if (!groups.has(label)) {
      groups.set(label, [])
      firstSeen.set(label, order++)
    }
    groups.get(label)!.push(p)
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    const ia = REGISTRY_GROEP_LABEL_VOLGORDE.indexOf(a)
    const ib = REGISTRY_GROEP_LABEL_VOLGORDE.indexOf(b)
    // Beide registry-groepen → vaste volgorde; anders registry vóór DB-only;
    // twee DB-only groepen → eerste-voorkomen-volgorde.
    if (ia !== -1 && ib !== -1) return ia - ib
    if (ia !== -1) return -1
    if (ib !== -1) return 1
    return (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0)
  })
}

// ── Server-side + client-side validatie ─────────────────────────────────────
//
// Gedeeld door de genereer-endpoints (API) en de formulieren (client). De
// validatie is bewust CONSERVATIEF: strikt op dropdown-/entity-opties
// (`hij_zij ∈ {Hij, Zij}`), maar lenient op getallen/datums zodat autofill-
// geformatteerde waarden ("€ 3.500,00", "26 mei 2026", "38") nooit een ONTERECHTE
// validatiefout geven (validatie-standaard: nooit een onterechte `Validatiefout`).
// Onbekende keys worden overgeslagen (tekstveld, geen validatie).

/** Eén validatiefout op placeholder-niveau. */
export interface PlaceholderValidationIssue {
  key: string
  message: string
}

/** Bevat de string ten minste één cijfer? (lenient getals-/datumcheck.) */
function heeftCijfer(v: string): boolean {
  return /\d/.test(v)
}

/**
 * Lenient decimal-check: na het strippen van valuta-/spatie-ruis mag er alleen
 * een getal met `.`/`,`-scheidingstekens overblijven. Accepteert "€ 3.500,00",
 * "3500", "3500,50", "1.234.56"; weigert "abc"/"veel". Nooit strikter, want de
 * autofill levert al NL-geformatteerde bedragen aan.
 */
function isDecimalLike(v: string): boolean {
  const cleaned = v.replace(/[€\s.]/g, '').replace(/,/g, '')
  return cleaned.length > 0 && /^\d+$/.test(cleaned)
}

/** Lenient int-check: cijfers (met optionele duizend-punten/spaties), geen komma-decimalen. */
function isIntLike(v: string): boolean {
  const cleaned = v.replace(/[\s.]/g, '')
  return cleaned.length > 0 && /^\d+$/.test(cleaned)
}

/**
 * Valideer de aangeleverde placeholder-waarden tegen de registry. Retourneert een
 * (mogelijk lege) lijst met per-veld fouten. Lege waarden en onbekende keys worden
 * overgeslagen (required-plicht + tekstveld-fallback horen elders).
 */
export function validatePlaceholderValues(
  values: Record<string, string>,
): PlaceholderValidationIssue[] {
  const issues: PlaceholderValidationIssue[] = []
  for (const [key, raw] of Object.entries(values)) {
    if (raw === null || raw === undefined) continue
    const value = String(raw).trim()
    if (value === '') continue
    const def = getPlaceholderFieldDef(key)
    if (!def) continue // onbekende key → tekstveld, niet valideren

    if ((def.type === 'select' || def.type === 'entity') && def.opties && def.opties.length > 0) {
      const toegestaan = def.opties.some((o) => o.value === value)
      if (!toegestaan) {
        const lijst = def.opties.map((o) => o.value).join(', ')
        issues.push({ key, message: `Ongeldige keuze. Kies een van: ${lijst}.` })
      }
      continue
    }
    if (def.type === 'decimal' && !isDecimalLike(value)) {
      issues.push({ key, message: 'Geef een geldig bedrag op (bijv. 3.500,00).' })
      continue
    }
    if (def.type === 'int' && !isIntLike(value)) {
      issues.push({ key, message: 'Geef een geheel getal op.' })
      continue
    }
    if (def.type === 'date' && !heeftCijfer(value)) {
      issues.push({ key, message: 'Geef een geldige datum op.' })
      continue
    }
  }
  return issues
}

/** Staat de verlenging-vlag (`is_verlenging`) aan in de waardenset? */
function isVerlengingAan(values: Record<string, string>): boolean {
  const flagNorm = normalizePlaceholderRegistryKey(CONDITIE_IS_VERLENGING)
  for (const [key, raw] of Object.entries(values)) {
    if (normalizePlaceholderRegistryKey(key) === flagNorm) {
      return String(raw ?? '').trim() !== ''
    }
  }
  return false
}

/**
 * FO-312 §5 - kruisveld-validatie voor het verlenging-blok: staat `is_verlenging`
 * aan, dan mogen de meegestuurde vorig-contract-velden (registry-conditie
 * `is_verlenging`) niet leeg zijn. Bij een eerste contract (vlag uit) geen eisen.
 * Retourneert per-veld issues (inline te tonen).
 */
export function validateVerlengingRequired(
  values: Record<string, string>,
): PlaceholderValidationIssue[] {
  if (!isVerlengingAan(values)) return []
  const issues: PlaceholderValidationIssue[] = []
  for (const [key, raw] of Object.entries(values)) {
    const def = getPlaceholderFieldDef(key)
    if (def?.conditie === CONDITIE_IS_VERLENGING && String(raw ?? '').trim() === '') {
      issues.push({ key, message: 'Verplicht bij een verlenging.' })
    }
  }
  return issues
}

